import Link from "next/link";
import { useState, useEffect, useMemo } from "react";
import Select, { StylesConfig } from "react-select";

interface OptionType {
    value: string;
    label: string;
    isDisabled?: boolean;
}

const customSelectStyles: StylesConfig<OptionType, false> = {
    control: (base, state) => ({
        ...base,
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        '&:focus': {
            border: '1px solid #e25926'
        },
        '&:hover': {
            border: '1px solid #e9ecef'
        },
        borderRadius: '8px',
        padding: '0.375rem 1rem',
        boxShadow: state.isFocused ? '0 0 0 1px #e25926' : 'none',
        fontSize: '18px', // Base font size
        fontWeight: 400,  // Base font weight
        color: '#495057 !important',
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '8px',
        overflow: 'hidden',
        zIndex: 5
    }),
    option: (base, state) => ({
        ...base,
        // Dark background if selected
        backgroundColor: state.isSelected ? '#000' : '#fff',
        // White text if selected, otherwise black/gray
        color: state.isSelected ? '#fff' : state.isDisabled ? '#dee2e6' : '#000',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        fontSize: '18px', // Option font size
        fontWeight: 400,  // Option font weight
        ':hover': {
            backgroundColor: state.isDisabled ? undefined : '#000',
            color: state.isDisabled ? undefined : '#fff'
        },
        ':active': {
            backgroundColor: state.isDisabled ? undefined : '#000'
        }
    }),
    singleValue: (base) => ({
        ...base,
        color: '#212529',
        fontSize: '18px', // Selected value font size
        fontWeight: 400, // Selected value font weight
    }),
    placeholder: (base) => ({
        ...base,
        fontSize: '18px', // Placeholder font size
        fontWeight: 400, // Placeholder font weight
        color: '#6b6b6b !important',
    })
};

interface MeetingFormData {
    name: string;
    email: string;
    company: string;
    preferredTime: string;
    message: string;
    ipAddress?: string;
    location?: {
        country?: string;
        region?: string;
        city?: string;
        timezone?: string;
    };
    userAgent?: string;
    agreedToCommunications: boolean;
}

interface FormErrors {
    name?: string;
    email?: string;
    company?: string;
    preferredTime?: string;
    message?: string;
    agreedToCommunications?: string;
}

const DateIcon = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19.25 9.16683H2.75M14.6667 1.8335V5.50016M7.33333 1.8335V5.50016M7.15 20.1668H14.85C16.3902 20.1668 17.1602 20.1668 17.7485 19.8671C18.266 19.6034 18.6866 19.1828 18.9503 18.6653C19.25 18.077 19.25 17.307 19.25 15.7668V8.06683C19.25 6.52668 19.25 5.75661 18.9503 5.16836C18.6866 4.65091 18.266 4.23021 17.7485 3.96656C17.1602 3.66683 16.3902 3.66683 14.85 3.66683H7.15C5.60985 3.66683 4.83978 3.66683 4.25153 3.96656C3.73408 4.23021 3.31338 4.65091 3.04973 5.16836C2.75 5.75661 2.75 6.52668 2.75 8.06683V15.7668C2.75 17.307 2.75 18.077 3.04973 18.6653C3.31338 19.1828 3.73408 19.6034 4.25153 19.8671C4.83978 20.1668 5.60985 20.1668 7.15 20.1668Z" stroke="#56595C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const TimeIcon = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.9997 5.50016V11.0002L14.6663 12.8335M20.1663 11.0002C20.1663 16.0627 16.0622 20.1668 10.9997 20.1668C5.93706 20.1668 1.83301 16.0627 1.83301 11.0002C1.83301 5.93755 5.93706 1.8335 10.9997 1.8335C16.0622 1.8335 20.1663 5.93755 20.1663 11.0002Z" stroke="#56595C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);





const MeetingForm = () => {
    interface TimeSlot {
        time: string;
        isBooked: boolean;
        note: string;
    }

    interface DayAvailability {
        date: string;
        slots: TimeSlot[];
    }

    // State for availability data
    const [availability, setAvailability] = useState<DayAvailability[]>([]);

    // Separate state for date and time
    const [selectedDate, setSelectedDate] = useState<string>("");
    const [selectedTime, setSelectedTime] = useState<string>("");

    const [formData, setFormData] = useState<MeetingFormData>({
        name: "",
        email: "",
        company: "",
        preferredTime: "",
        message: "",
        ipAddress: undefined,
        location: undefined,

        userAgent: undefined,
        agreedToCommunications: false,
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{
        type: 'success' | 'error' | null;
        message: string;
    }>({ type: null, message: '' });
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Fetch availability on mount
    useEffect(() => {
        const fetchAvailability = async () => {
            try {
                const res = await fetch('/api/1bfs-contact');
                if (res.ok) {
                    const data: DayAvailability[] = await res.json();
                    setAvailability(data);

                    // Auto-select first available date and time
                    const availableDay = data.find(d => d.slots.some(s => !s.isBooked));
                    if (availableDay) {
                        setSelectedDate(availableDay.date);
                        const availableSlot = availableDay.slots.find(s => !s.isBooked);
                        if (availableSlot) {
                            setSelectedTime(availableSlot.time);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch availability", err);
            }
        };
        fetchAvailability();
    }, []);

    // Get slots for selected date (both booked and unbooked)
    const dayTimeSlots = useMemo(() => {
        if (!selectedDate) return [];
        const day = availability.find(d => d.date === selectedDate);
        return day ? day.slots : [];
    }, [selectedDate, availability]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        }

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        // Company validation
        if (!formData.company.trim()) {
            newErrors.company = "Company name is required";
        }

        // Preferred time validation
        if (!selectedDate || !selectedTime) {
            newErrors.preferredTime = "Please select both date and time";
        }

        // Message validation
        if (!formData.message.trim()) {
            newErrors.message = "Please tell us what you're building";
        }

        // Agreement validation
        if (!formData.agreedToCommunications) {
            newErrors.agreedToCommunications = "You must agree to receive communications";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    // Function to get user's IP and location
    const getUserLocationData = async () => {
        try {
            // Get IP address
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            const ipData = await ipResponse.json();

            // Get location data from IP
            const locationResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
            const locationData = await locationResponse.json();

            return {
                ipAddress: ipData.ip,
                location: {
                    country: locationData.country_name,
                    region: locationData.region,
                    city: locationData.city,
                    timezone: locationData.timezone,
                },
                userAgent: navigator.userAgent,
            };
        } catch (error) {
            console.warn('Could not fetch location data:', error);
            return {
                ipAddress: undefined,
                location: undefined,
                userAgent: navigator.userAgent,
            };
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus({ type: null, message: '' });

        try {
            // Construct the final datetime string
            const finalDateTime = `${selectedDate} ${selectedTime}`;

            // Get user location data
            const locationData = await getUserLocationData();

            const payload = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                company: formData.company.trim(),
                preferredTime: finalDateTime,
                message: formData.message.trim(),
                ipAddress: locationData.ipAddress,
                location: locationData.location,
                userAgent: locationData.userAgent,
            };

            const response = await fetch('/api/1bfs-contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                setSubmitStatus({
                    type: 'success',
                    message: 'Thank you! We\'ll see you at 1BFS.'
                });
                setSubmitSuccess(true);
                // Reset form
                setFormData({
                    name: "",
                    email: "",
                    company: "",
                    preferredTime: "",
                    message: "",
                    ipAddress: undefined,
                    location: undefined,
                    userAgent: undefined,
                    agreedToCommunications: false,
                });
                setSelectedDate("");
                setSelectedTime("");

                // Refresh availability to reflect the newly booked slot
                const res = await fetch('/api/1bfs-contact');
                if (res.ok) {
                    const avData = await res.json();
                    setAvailability(avData);
                }

            } else {
                setSubmitStatus({
                    type: 'error',
                    message: (data && data.error) || 'Something went wrong. Please try again.'
                });
                setSubmitSuccess(false); // Ensure success state is false on error
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus({
                type: 'error',
                message: 'Network error. Please check your connection and try again.'
            });
            setSubmitSuccess(false); // Ensure success state is false on error
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section
            id="meeting-form"
            className="section-padding position-relative"
            style={{
                backgroundImage: 'url("/images/hero/form_bg.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center'
            }}
        >
            {/* Overlay */}
            <div
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1 }}
            ></div>

            <div className="container position-relative my-5" style={{ zIndex: 2 }}>
                <div className="row justify-content-center">
                    <div className="col-lg-6 col-md-8 col-12">
                        <div className="bg-white p-4 p-md-5">
                            {submitSuccess ? (
                                <div className="text-center">
                                    <div className="thank-you-icon mb-4 d-flex justify-content-center">
                                        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M35.2067 48.3067L26.8867 39.9867C26.5733 39.6756 26.19 39.5089 25.7367 39.4867C25.2833 39.4644 24.88 39.6311 24.5267 39.9867C24.1733 40.3422 23.9956 40.7356 23.9933 41.1667C23.9911 41.5978 24.1689 41.9911 24.5267 42.3467L33.32 51.14C33.8578 51.68 34.4856 51.95 35.2033 51.95C35.9211 51.95 36.55 51.68 37.09 51.14L55.3467 32.8867C55.6578 32.5733 55.8244 32.19 55.8467 31.7367C55.8689 31.2833 55.7022 30.88 55.3467 30.5267C54.9911 30.1733 54.5978 29.9956 54.1667 29.9933C53.7356 29.9911 53.3422 30.1689 52.9867 30.5267L35.2067 48.3067ZM40.01 70C35.8633 70 31.9633 69.2133 28.31 67.64C24.6589 66.0644 21.4822 63.9267 18.78 61.2267C16.0778 58.5267 13.9389 55.3533 12.3633 51.7067C10.7878 48.06 10 44.1611 10 40.01C10 35.8589 10.7878 31.9589 12.3633 28.31C13.9367 24.6589 16.0711 21.4822 18.7667 18.78C21.4622 16.0778 24.6367 13.9389 28.29 12.3633C31.9433 10.7878 35.8433 10 39.99 10C44.1367 10 48.0367 10.7878 51.69 12.3633C55.3411 13.9367 58.5178 16.0722 61.22 18.77C63.9222 21.4678 66.0611 24.6422 67.6367 28.2933C69.2122 31.9444 70 35.8433 70 39.99C70 44.1367 69.2133 48.0367 67.64 51.69C66.0667 55.3433 63.9289 58.52 61.2267 61.22C58.5244 63.92 55.3511 66.0589 51.7067 67.6367C48.0622 69.2144 44.1633 70.0022 40.01 70ZM40 66.6667C47.4444 66.6667 53.75 64.0833 58.9167 58.9167C64.0833 53.75 66.6667 47.4444 66.6667 40C66.6667 32.5556 64.0833 26.25 58.9167 21.0833C53.75 15.9167 47.4444 13.3333 40 13.3333C32.5556 13.3333 26.25 15.9167 21.0833 21.0833C15.9167 26.25 13.3333 32.5556 13.3333 40C13.3333 47.4444 15.9167 53.75 21.0833 58.9167C26.25 64.0833 32.5556 66.6667 40 66.6667Z" fill="#31A24C" />
                                        </svg>
                                    </div>
                                    <h3 className="thank-you-title">See you there!</h3>
                                    <p className="thank-you-message">
                                        We&rsquo;ve confirmed your meeting time. Check your email for details.
                                    </p>
                                    <button
                                        onClick={() => setSubmitSuccess(false)}
                                        className="desilo-link-btn btn-pill w-100 text-center justify-content-center mt-3"
                                    >
                                        Book another
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <h3 className="fw-bold mb-1">Let&rsquo;s meet at 1BFS</h3>
                                    <p className="text-secondary mb-4 fs-6 lh-base">
                                        Book a time during the summit or <Link href="https://wa.me/919033939547?text=Hi,%20I’d%20like%20to%20connect%20with%20you." target="_blank" className="text-decoration-underline text-dark">connect directly via WhatsApp</Link>.
                                    </p>
                                    <form onSubmit={handleSubmit} noValidate>
                                        {submitStatus.type === 'error' && (
                                            <div className="alert alert-danger mb-4">
                                                {submitStatus.message}
                                            </div>
                                        )}
                                        <div className="row g-3">
                                            {/* Name */}
                                            <div className="col-12">
                                                <input
                                                    type="text"
                                                    name="name"
                                                    placeholder="Name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    className="form-control modern-input py-3 px-4 bg-light"
                                                    disabled={isSubmitting}
                                                />
                                                {errors.name && <div className="text-danger mt-1 small">{errors.name}</div>}
                                            </div>

                                            {/* Email */}
                                            <div className="col-12">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    placeholder="Email address"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className="form-control modern-input py-3 px-4 bg-light"
                                                    disabled={isSubmitting}
                                                />
                                                {errors.email && <div className="text-danger mt-1 small">{errors.email}</div>}
                                            </div>

                                            {/* Company */}
                                            <div className="col-12">
                                                <input
                                                    type="text"
                                                    name="company"
                                                    placeholder="Company name"
                                                    value={formData.company}
                                                    onChange={handleInputChange}
                                                    className="form-control modern-input py-3 px-4 bg-light"
                                                    disabled={isSubmitting}
                                                />
                                                {errors.company && <div className="text-danger mt-1 small">{errors.company}</div>}
                                            </div>

                                            {/* Date and Time Selection */}
                                            <div className="col-xl-6 col-12">
                                                <Select<OptionType>
                                                    instanceId="date-select"
                                                    components={{
                                                        DropdownIndicator: () => <div className="d-flex align-items-center"><DateIcon /></div>
                                                    }}
                                                    options={availability.map(day => ({
                                                        value: day.date,
                                                        label: new Date(day.date).toLocaleDateString('en-GB', {
                                                            day: 'numeric',
                                                            month: 'long',
                                                            year: 'numeric'
                                                        }),
                                                        isDisabled: day.slots.every(slot => slot.isBooked)
                                                    }))}
                                                    isOptionDisabled={(option) => !!option.isDisabled}
                                                    value={selectedDate ? {
                                                        value: selectedDate,
                                                        label: new Date(selectedDate).toLocaleDateString('en-GB', {
                                                            day: 'numeric',
                                                            month: 'long',
                                                            year: 'numeric'
                                                        })
                                                    } : null}
                                                    onChange={(option) => {
                                                        const newDate = option?.value || "";
                                                        setSelectedDate(newDate);

                                                        // Auto-select first available time for the new date
                                                        if (newDate) {
                                                            const day = availability.find(d => d.date === newDate);
                                                            const firstSlot = day?.slots.find(s => !s.isBooked);
                                                            setSelectedTime(firstSlot?.time || "");
                                                        } else {
                                                            setSelectedTime("");
                                                        }

                                                        if (errors.preferredTime) {
                                                            setErrors(prev => ({ ...prev, preferredTime: undefined }));
                                                        }
                                                    }}
                                                    placeholder="Select Date"
                                                    isDisabled={isSubmitting || availability.length === 0}
                                                    styles={customSelectStyles}
                                                    theme={(theme) => ({
                                                        ...theme,
                                                        colors: {
                                                            ...theme.colors,
                                                            primary: '#000',
                                                        },
                                                    })}
                                                />
                                            </div>
                                            <div className="col-xl-6 col-12">
                                                <Select<OptionType>
                                                    instanceId="time-select"
                                                    components={{
                                                        DropdownIndicator: () => <div className="d-flex align-items-center"><TimeIcon /></div>
                                                    }}
                                                    options={dayTimeSlots.map(slot => ({
                                                        value: slot.time,
                                                        label: `${slot.time} ${slot.isBooked ? '(Booked)' : ''}`,
                                                        isDisabled: slot.isBooked
                                                    }))}
                                                    value={selectedTime ? {
                                                        value: selectedTime,
                                                        label: selectedTime
                                                    } : null}
                                                    onChange={(option) => {
                                                        setSelectedTime(option?.value || "");
                                                        if (errors.preferredTime) {
                                                            setErrors(prev => ({ ...prev, preferredTime: undefined }));
                                                        }
                                                    }}
                                                    placeholder="Select Time"
                                                    isDisabled={isSubmitting || !selectedDate}
                                                    isOptionDisabled={(option) => !!option.isDisabled}
                                                    styles={customSelectStyles}
                                                    theme={(theme) => ({
                                                        ...theme,
                                                        colors: {
                                                            ...theme.colors,
                                                            primary: '#000',
                                                        },
                                                    })}
                                                />
                                            </div>
                                            {/* Combined error message for Date/Time */}
                                            {(errors.preferredTime) && <div className="col-12 text-danger mt-1 small">{errors.preferredTime}</div>}

                                            {/* Message */}
                                            <div className="col-12">
                                                <textarea
                                                    name="message"
                                                    rows={4}
                                                    placeholder="Write your message here"
                                                    value={formData.message}
                                                    onChange={handleInputChange}
                                                    className="form-control modern-input py-3 px-4 bg-light"
                                                    disabled={isSubmitting}
                                                />
                                                {errors.message && <div className="text-danger mt-1 small">{errors.message}</div>}
                                            </div>

                                            {/* Terms Agreement */}
                                            <div className="col-12">
                                                <p className="text-secondary small mb-2 text-start fs-6 lh-base">
                                                    We&rsquo;ll only use your personal data to share information and provide the services you requested from us.
                                                </p>
                                                <div className="form-check text-start">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"

                                                        id="agreedToCommunications"
                                                        name="agreedToCommunications"
                                                        checked={formData.agreedToCommunications}
                                                        onChange={handleInputChange}
                                                        disabled={isSubmitting}
                                                    />
                                                    <label className="form-check-label small fw-medium text-dark" htmlFor="agreedToCommunications">
                                                        I agree to receive communication from Desilo
                                                    </label>
                                                </div>
                                                {errors.agreedToCommunications && <div className="text-danger mt-1 small text-start">{errors.agreedToCommunications}</div>}
                                            </div>

                                            {/* Buttons */}
                                            <div className="col-12 mt-4 text-center">
                                                <button
                                                    type="submit"
                                                    className="desilo-link-btn btn-pill w-100 text-center justify-content-center mb-2"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm text-white" role="status" aria-hidden="true"></span>
                                                            Sending...
                                                        </>
                                                    ) : (
                                                        'Book a meeting'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MeetingForm;
